import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Shield, Mail, Check, X } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

/**
 * Patient-side modal that pops up when a doctor has requested access to the file.
 * Patient enters the OTP they received by email to accept.
 */
export function DoctorAccessRequestPopup({ onConsultationCreated }) {
    const { t } = useTranslation();
    const [requests, setRequests] = useState([]);
    const [active, setActive] = useState(null);
    const [otp, setOtp] = useState("");
    const [busy, setBusy] = useState(false);
    // Requests the user has explicitly dismissed in this tab — don't auto re-open
    const [dismissed, setDismissed] = useState(() => new Set());

    const load = async () => {
        try {
            const { data } = await api.get("/access-requests/mine");
            setRequests(data || []);
            // auto-open the latest pending one that hasn't been dismissed
            const pending = (data || []).find((r) => r.status === "pending" && !dismissed.has(r.id));
            if (pending && !active) setActive(pending);
        } catch {/* silent */}
    };

    useEffect(() => {
        load();
        const t = setInterval(load, 10000);
        return () => clearInterval(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dismissed]);

    if (!active) return null;

    const dismiss = () => {
        setDismissed((d) => new Set([...d, active.id]));
        setActive(null);
        setOtp("");
    };

    const respond = async (accept) => {
        setBusy(true);
        try {
            const { data } = await api.post(`/access-requests/${active.id}/respond`, {
                otp: otp.trim(),
                accept,
            });
            if (accept) {
                toast.success("Doctor unlocked. Opening consultation…");
                onConsultationCreated?.(data.consultation);
            } else {
                toast.success("Request declined.");
            }
            setActive(null);
            setOtp("");
            await load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not respond");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[70] bg-black/60 grid place-items-center p-4" data-testid="doctor-access-popup">
            <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full p-7 relative">
                <button className="absolute top-3 right-3 p-2 rounded-full hover:bg-gray-100" onClick={dismiss} aria-label="Close" data-testid="doctor-access-close">
                    <X className="w-4 h-4" />
                </button>
                <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[var(--medx-red)] to-[var(--medx-orange)] grid place-items-center text-white">
                        <Shield className="w-6 h-6" />
                    </div>
                    <div>
                        <div className="text-xs font-mono tracking-[0.2em] text-[var(--medx-red)]">DOCTOR ACCESS REQUEST</div>
                        <div className="font-display text-lg font-semibold">{active.doctor_name}</div>
                    </div>
                </div>
                <p className="text-sm text-gray-700 mb-4" data-testid="doctor-access-reason">
                    {t("consult.request.patientPrompt", { doctor: active.doctor_name, reason: active.reason })}
                </p>
                <div className="text-xs text-gray-500 mb-3 flex items-center gap-2 bg-orange-50 border border-orange-100 rounded-lg p-3">
                    <Mail className="w-4 h-4 text-[var(--medx-orange)] shrink-0" />
                    <span>We've emailed a 6-digit OTP to <strong>{active.patient_email}</strong>. Enter it below to accept.</span>
                </div>
                <label className="block text-xs font-mono tracking-wider text-gray-500 mb-1">
                    {t("consult.request.enterOtp").toUpperCase()}
                </label>
                <input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                    placeholder="••••••"
                    className="w-full text-center tracking-[0.5em] text-2xl font-mono font-bold px-4 py-3 rounded-xl border border-gray-200 focus:border-[var(--medx-orange)] outline-none"
                    data-testid="doctor-access-otp-input"
                />
                <div className="grid grid-cols-2 gap-3 mt-4">
                    <button
                        onClick={() => respond(false)}
                        disabled={busy}
                        className="btn-outline inline-flex items-center justify-center gap-2 !py-2.5"
                        data-testid="doctor-access-decline"
                    >
                        <X className="w-4 h-4" /> {t("consult.request.decline")}
                    </button>
                    <button
                        onClick={() => respond(true)}
                        disabled={busy || otp.length !== 6}
                        className="btn-primary inline-flex items-center justify-center gap-2 !py-2.5 disabled:opacity-50"
                        data-testid="doctor-access-accept"
                    >
                        <Check className="w-4 h-4" /> {t("consult.request.acceptShareOtp")}
                    </button>
                </div>
            </div>
        </div>
    );
}
